import type {
  MeetingNotes,
  MeetingNotesContent,
  MeetingTranscript,
  RoomWebhookView,
  WebhookDeliveryView,
  WebhookEventType,
} from '@live-stream/shared'
import type { HttpClient } from '../http.js'
import { MeetingPage } from '../meetings/page.js'
import type { ReadableStore } from '../store.js'
import type { Rooms } from './rooms.js'

/** Signed download links for a meeting's notes, good for a few minutes. */
export interface ExportLinks {
  markdown: string
  pdf: string
  markdownWithTranscript: string
  pdfWithTranscript: string
}

/** A past meeting: its transcript, Claude's notes on it, and what to take away. */
export class Meetings {
  constructor(
    private readonly http: HttpClient,
    private readonly rooms: Rooms,
    private readonly signedIn: ReadableStore<boolean>,
  ) {}

  /** A meeting's page: its transcript, recording and notes. `open(sessionId)` it. */
  page(): MeetingPage {
    return new MeetingPage(this, this.rooms, this.signedIn)
  }

  async transcript(sessionId: string): Promise<MeetingTranscript> {
    return (await this.http.get<{ transcript: MeetingTranscript }>(`/transcripts/${sessionId}`))
      .transcript
  }

  /** Hosts: delete the transcript, and the notes with it. */
  async deleteTranscript(sessionId: string): Promise<void> {
    await this.http.delete(`/transcripts/${sessionId}`)
  }

  /** Hosts: have Claude write the notes (again). They come back pending. */
  async writeNotes(sessionId: string): Promise<MeetingNotes> {
    return (await this.http.post<{ notes: MeetingNotes }>(`/transcripts/${sessionId}/notes`)).notes
  }

  /** Members: save an edit of the notes; the original Claude wrote is kept. */
  async editNotes(sessionId: string, content: MeetingNotesContent): Promise<MeetingNotes> {
    return (
      await this.http.patch<{ notes: MeetingNotes }>(`/transcripts/${sessionId}/notes`, { content })
    ).notes
  }

  /** Links to download the notes as Markdown or PDF, with or without the transcript. */
  async exportLinks(sessionId: string): Promise<ExportLinks> {
    return (await this.http.get<{ links: ExportLinks }>(`/transcripts/${sessionId}/exports`)).links
  }

  /**
   * A URL to download the notes from, good for a few minutes: a plain link
   * carries no account, so the server signs one. `transcript` adds the
   * transcript after the notes; dates are written in `timeZone`.
   */
  async downloadUrl(
    sessionId: string,
    format: 'md' | 'pdf',
    opts: { transcript: boolean; timeZone?: string },
  ): Promise<string> {
    const links = await this.exportLinks(sessionId)
    const link =
      format === 'md'
        ? opts.transcript
          ? links.markdownWithTranscript
          : links.markdown
        : opts.transcript
          ? links.pdfWithTranscript
          : links.pdf
    const zoned = opts.timeZone ? `${link}&tz=${encodeURIComponent(opts.timeZone)}` : link
    return link.startsWith('/') ? this.http.rootUrl(zoned) : zoned
  }
}

export interface WebhookInput {
  url: string
  events: WebhookEventType[]
  enabled: boolean
}

/** A room's outgoing webhooks: hosts only. */
export class Webhooks {
  constructor(private readonly http: HttpClient) {}

  list(slug: string): Promise<{ webhooks: RoomWebhookView[]; max: number }> {
    return this.http.get(`/webhooks/by-room/${slug}`)
  }

  /** A new webhook, and its signing secret, shown this once. */
  create(slug: string, input: WebhookInput): Promise<{ webhook: RoomWebhookView; secret: string }> {
    return this.http.post(`/webhooks/by-room/${slug}`, input)
  }

  async update(id: string, change: Partial<WebhookInput>): Promise<RoomWebhookView> {
    return (await this.http.patch<{ webhook: RoomWebhookView }>(`/webhooks/${id}`, change)).webhook
  }

  async remove(id: string): Promise<void> {
    await this.http.delete(`/webhooks/${id}`)
  }

  async secret(id: string): Promise<string> {
    return (await this.http.get<{ secret: string }>(`/webhooks/${id}/secret`)).secret
  }

  /** A new secret; the old one stops working at once. */
  regenerateSecret(id: string): Promise<{ webhook: RoomWebhookView; secret: string }> {
    return this.http.post(`/webhooks/${id}/secret`)
  }

  /** Send a test event now, and say how it went. */
  async test(id: string): Promise<WebhookDeliveryView> {
    return (await this.http.post<{ delivery: WebhookDeliveryView }>(`/webhooks/${id}/test`))
      .delivery
  }
}
